import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { 
  Workflow, 
  Zap, 
  Clock, 
  CheckCircle, 
  ArrowRight,
  Bot,
  Mail,
  Calendar,
  FileText,
  Database,
  Shield,
  Globe,
  Target,
  Users,
  TrendingUp,
  Code,
  Smartphone,
  Settings
} from 'lucide-react';

const PowerAutomate: React.FC = () => {
  const automationSolutions = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Automation",
      description: "Automate email workflows, approvals, and notifications across your organization.",
      features: ["Automated responses", "Approval workflows", "Email templates", "Scheduled sending"]
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Document Processing",
      description: "Streamline document creation, approval, and management processes.",
      features: ["Document generation", "Approval routing", "Version control", "Digital signatures"]
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Data Integration",
      description: "Connect and synchronize data across multiple systems and platforms.",
      features: ["Real-time sync", "Data transformation", "Multi-system integration", "Error handling"]
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Scheduling & Reminders",
      description: "Automate scheduling tasks, reminders, and time-based workflows.",
      features: ["Meeting scheduling", "Automated reminders", "Task assignments", "Deadline tracking"]
    }
  ];

  const benefits = [
    "Reduce manual tasks by up to 80%",
    "Improve process consistency and accuracy",
    "Save 20+ hours per week per employee",
    "Eliminate human errors in repetitive tasks",
    "Increase team productivity and focus",
    "Ensure compliance with business rules"
  ];

  const useCases = [
    {
      title: "Sales Process Automation",
      description: "Automate lead qualification, follow-ups, and opportunity management.",
      icon: <Target className="w-8 h-8" />
    },
    {
      title: "HR Onboarding",
      description: "Streamline employee onboarding with automated workflows and approvals.",
      icon: <Users className="w-8 h-8" />
    },
    {
      title: "Invoice Processing",
      description: "Automate invoice approvals, payments, and accounting integrations.",
      icon: <FileText className="w-8 h-8" />
    },
    {
      title: "Customer Support",
      description: "Automate ticket routing, escalations, and response workflows.",
      icon: <Bot className="w-8 h-8" />
    },
    {
      title: "Compliance Reporting",
      description: "Generate automated compliance reports and audit trails.",
      icon: <Shield className="w-8 h-8" />
    },
    {
      title: "Data Backup & Sync",
      description: "Automate data backups and synchronization across systems.",
      icon: <Database className="w-8 h-8" />
    }
  ];

  const features = [
    "300+ pre-built connectors",
    "AI-powered automation suggestions",
    "Visual workflow designer",
    "Mobile app for approvals",
    "Enterprise-grade security",
    "Advanced analytics and monitoring"
  ];

  return (
    <>
      <SEOHead
        title="Power Automate Solutions | CRMONCE - Workflow Automation Services"
        description="Transform your business with Microsoft Power Automate. Expert workflow automation, process optimization, and integration services. Reduce manual tasks by 80% with CRMONCE."
        keywords="Power Automate, workflow automation, business process automation, Microsoft Power Platform, process optimization, workflow management, automation consulting, RPA, robotic process automation, flow automation, Microsoft Flow"
        canonical="https://crmonce.com/power-automate"
        ogImage="/images/power-automate-og.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Microsoft Power Automate Solutions",
          "description": "Professional Microsoft Power Automate workflow automation and business process optimization services. Reduce manual tasks by 80% with intelligent automation.",
          "provider": {
            "@type": "Organization",
            "name": "CRMONCE",
            "url": "https://crmonce.com",
            "logo": "https://crmonce.com/logo.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-8096556344",
              "contactType": "sales",
              "email": "info@crmonce.com"
            }
          },
          "serviceType": "Workflow Automation",
          "category": "Business Process Automation",
          "areaServed": {
            "@type": "Place",
            "name": "Worldwide"
          },
          "offers": {
            "@type": "Offer",
            "description": "Professional Power Automate implementation and consulting services",
            "category": "Consulting Services"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Power Automate Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Email Automation",
                  "description": "Automate email workflows, approvals, and notifications"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Document Processing",
                  "description": "Streamline document creation, approval, and management"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Data Integration",
                  "description": "Connect and synchronize data across multiple systems"
                }
              }
            ]
          }
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* Hero Section */}
        <section className="pt-48 pb-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="flex items-center justify-center mb-6">
                <Workflow className="w-12 h-12 mr-4" />
                <h1 className="text-5xl font-bold">Power Automate Solutions</h1>
              </div>
              <p className="text-xl text-blue-100 mb-8">
                Transform your business processes with intelligent automation. Eliminate repetitive tasks, 
                reduce errors, and free your team to focus on high-value work with Microsoft Power Automate.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all duration-300"
              >
                Start Automation Journey
                <ArrowRight className="ml-2 w-5 h-5 inline" />
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Automation Solutions */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Comprehensive Automation Solutions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From simple task automation to complex business processes, we help you automate 
                every aspect of your operations with Power Automate.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {automationSolutions.map((solution, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-blue-600 mb-4">{solution.icon}</div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    {solution.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {solution.description}
                  </p>
                  <div className="space-y-2">
                    {solution.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Real-World Use Cases
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See how businesses across industries are transforming their operations 
                with Power Automate workflows.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-blue-600 mb-4">
                    {useCase.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{useCase.title}</h3>
                  <p className="text-gray-600">{useCase.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Why Choose Power Automate?
              </h2>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Transform your business operations with intelligent automation that scales with your needs.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="flex items-center bg-white bg-opacity-10 rounded-lg p-6"
                >
                  <CheckCircle className="w-6 h-6 text-green-400 mr-4 flex-shrink-0" />
                  <span className="text-white font-medium">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Powerful Features
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Power Automate comes packed with enterprise-grade features to handle 
                any automation challenge your business faces.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="flex items-center bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6"
                >
                  <Zap className="w-6 h-6 text-blue-600 mr-4 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">{feature}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-gray-900 to-blue-900">
          <div className="container-custom text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Automate Your Business?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Let our Power Automate experts help you identify automation opportunities 
                and implement workflows that drive real business value.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Get Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
                >
                  View Case Studies
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PowerAutomate;
